import { REPORT_PALETTE } from '@modules/consolidator/constants/Color'
import { resolveGroupLabel } from '@modules/consolidator/utils/resolve-group-label/resolve-group-label'
import { resolveAssetGroupKey } from '@modules/consolidator/utils/resolve-asset-group-key/resolve-asset-group-key'
import {
  isPosicaoZeroed,
  resolveAtivoPl,
  resolveGrupoPl,
} from '@modules/consolidator/utils/posicao-pl-fallback/posicao-pl-fallback'

export const formatAlocacaoEstrategicaChart = (
  walletAnalysis,
  getColor,
  { groupLabelByKey = null, groupBy = null, expandFundoComposto = false } = {}
) => {
  const empty = { values: [], opts: { labels: [], colors: [] } }
  const posicao = walletAnalysis && walletAnalysis.posicao_completa
  const grupos = posicao && posicao.grupos
  if (!Array.isArray(grupos) || grupos.length === 0) {
    return empty
  }
  const zeroed = isPosicaoZeroed(walletAnalysis)
  const entries = groupBy
    ? buildEntriesByGroupBy(grupos, zeroed, groupBy, groupLabelByKey)
    : buildEntriesByPosicao(grupos, zeroed, groupLabelByKey)
  const suggestedOnly = isClassGrouped(groupBy)
    ? getSuggestedOnlyClasses(
        walletAnalysis,
        entries.map((entry) => entry.name),
        expandFundoComposto
      )
    : []
  const allEntries = entries.concat(suggestedOnly)
  const labels = allEntries.map((entry) => entry.name)
  const values = allEntries.map((entry) => entry.value)
  const colors = allEntries.map((entry, index) =>
    getColor ? getColor(entry.name, index) : ''
  )
  return { values, opts: { labels, colors } }
}

const CLASS_GROUP_KEYS = new Set([null, undefined, '', 'classe', 'subclasse'])

export const isClassGrouped = (groupBy) => CLASS_GROUP_KEYS.has(groupBy)

export const getSuggestedOnlyClasses = (
  walletAnalysis,
  existingLabels,
  expandFundoComposto = false
) => {
  const fracoes = pickSuitabilityFracoes(walletAnalysis, expandFundoComposto)
  if (!fracoes) return []
  const existing = new Set(Array.isArray(existingLabels) ? existingLabels : [])
  return Object.keys(fracoes).reduce((acc, key) => {
    const fracao = fracoes[key]
    const composicao = fracao && fracao.composicao_perfil
    const hasSuggestion = typeof composicao === 'number' && composicao > 0
    if (hasSuggestion && !existing.has(key)) {
      acc.push({ name: key, value: 0 })
    }
    return acc
  }, [])
}

const buildEntriesByPosicao = (grupos, zeroed, groupLabelByKey) =>
  grupos.map((grupo) => {
    const name = (grupo && grupo.grupo) || ''
    return {
      name: resolveGroupLabel(name, groupLabelByKey) || name,
      value: resolveGrupoPl(grupo, zeroed),
    }
  })

const buildEntriesByGroupBy = (grupos, zeroed, groupBy, groupLabelByKey) => {
  const totals = {}
  const order = []
  grupos.forEach((grupo) => {
    const ativos = grupo && Array.isArray(grupo.ativos) ? grupo.ativos : []
    ativos.forEach((ativo) => {
      const key = resolveAssetGroupKey(ativo, groupBy, groupLabelByKey)
      if (!key) return
      if (!(key in totals)) {
        totals[key] = 0
        order.push(key)
      }
      totals[key] += resolveAtivoPl(ativo, zeroed)
    })
  })
  return order
    .map((key) => ({ name: key, value: totals[key] }))
    .sort((a, b) => b.value - a.value)
}

export const formatSuggestedAllocations = (
  walletAnalysis,
  labels,
  { expandFundoComposto = false } = {}
) => {
  const fracoes = pickSuitabilityFracoes(walletAnalysis, expandFundoComposto)
  if (!fracoes) return []
  const list = Array.isArray(labels) ? labels : []
  return list.map((label) => {
    const fracao = fracoes[label]
    if (!fracao) return 0
    const composicao = fracao.composicao_perfil
    return typeof composicao === 'number' ? composicao * 100 : 0
  })
}

const pickSuitabilityFracoes = (walletAnalysis, expandFundoComposto) => {
  const suitability = walletAnalysis && walletAnalysis.suitability
  if (!suitability) return null
  if (expandFundoComposto && suitability.fracoes_expanded) {
    return suitability.fracoes_expanded
  }
  return suitability.fracoes || null
}

export const buildClassColorMap = (labels) => {
  const list = Array.isArray(labels) ? labels.filter(Boolean) : []
  const sorted = [...list].map(String).sort((a, b) => a.localeCompare(b))
  const map = {}
  sorted.forEach((label, i) => {
    map[label] = REPORT_PALETTE[i % REPORT_PALETTE.length]
  })
  return map
}
